import { useEffect, useState } from "react";
import EmailIcon from "@mui/icons-material/Email";
import LocalPhoneIcon from "@mui/icons-material/LocalPhone";
import AddLocationAltIcon from "@mui/icons-material/AddLocationAlt";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import LogoutIcon from "@mui/icons-material/Logout";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import AppShell from "../components/AppShell";
import MobileHeader from "../components/MobileHeader";
import ProfilePhotoPicker, {
  PHOTO_UPDATED_EVENT,
} from "../components/ProfilePhotoPicker";
import { PROFILE_UPDATED_EVENT } from "../components/profileEvents";
import ApiMethods from "../../../../ApiManager/ApiMethods";
import { useAlert } from "../../../context/AlertContext";
import "./profileShell.css";
import NotificationsNoneIcon from "@mui/icons-material/NotificationsNone";
import {
  unregisterPushToken,
  registerPushToken,
  getPushStatus,
} from "../../../../firebase";

// The logged-in trainer/admin's own profile — NOT a member from the Members list. Fetches the
// authenticated employee's own record via `GET employee/me` (identified server-side from the
// auth token, not a client-suppliable id — unlike `GET users/:id`, which is web_admin's
// role-gated CRUD endpoint for managing *other* employees and was a layering mistake to reuse
// here) and lets them edit it, reusing the existing ProfilePhotoPicker for the avatar and the
// same read-only-email / gender-chip pattern already established for member editing
// (PopUpTest.jsx).
const Profile = () => {
  const { showAlert } = useAlert();
  const token = localStorage.getItem("token");

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [photoUrl, setPhotoUrl] = useState(
    () => localStorage.getItem("photoUrl") || null
  );

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [gender, setGender] = useState({ male: false, female: false });
  const [nickname, setNickname] = useState("");
  const [address, setAddress] = useState("");
  // Snapshot of what's actually saved server-side (not just the initial load — also re-taken
  // after a successful save, below) — compared against the live fields to gate the save button,
  // so it's only enabled while there's something new to actually send.
  const [savedProfile, setSavedProfile] = useState(null);

  // Push-notification state for THIS browser (see firebase.js getPushStatus) — null until known.
  const [pushStatus, setPushStatus] = useState(null);
  const [pushBusy, setPushBusy] = useState(false);

  useEffect(() => {
    getPushStatus().then(setPushStatus);
  }, []);

  const handleSubscribePush = async () => {
    setPushBusy(true);
    const ok = await registerPushToken({ prompt: true });
    const status = await getPushStatus();
    setPushStatus(status);
    setPushBusy(false);
    if (ok) {
      showAlert("Push obaveštenja su uključena na ovom uređaju", "success");
    } else if (status === "denied") {
      showAlert("Obaveštenja su blokirana u podešavanjima pregledača", "error");
    } else {
      showAlert("Nije moguće uključiti push obaveštenja", "error");
    }
  };

  const initials = `${firstName[0] || ""}${lastName[0] || ""}`.toUpperCase();
  const genderValue = gender.male ? "Male" : gender.female ? "Female" : null;
  const isDirty =
    !!savedProfile &&
    (firstName !== savedProfile.firstName ||
      lastName !== savedProfile.lastName ||
      phone !== savedProfile.phone ||
      nickname !== savedProfile.nickname ||
      address !== savedProfile.address ||
      genderValue !== savedProfile.gender);

  useEffect(() => {
    const onPhotoUpdated = (e) => setPhotoUrl(e.detail.photoUrl);
    window.addEventListener(PHOTO_UPDATED_EVENT, onPhotoUpdated);
    return () =>
      window.removeEventListener(PHOTO_UPDATED_EVENT, onPhotoUpdated);
  }, []);

  useEffect(() => {
    if (!token) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    ApiMethods.get(`employee/me`, token)
      .then((profile) => {
        if (cancelled || !profile) return;
        setFirstName(profile.first_name || "");
        setLastName(profile.last_name || "");
        setEmail(profile.email || "");
        setPhone(profile.phone != null ? String(profile.phone) : "");
        setGender({
          male: profile.gender === "Male",
          female: profile.gender === "Female",
        });
        setNickname(profile.nickname || "");
        setAddress(profile.address || "");
        setSavedProfile({
          firstName: profile.first_name || "",
          lastName: profile.last_name || "",
          phone: profile.phone != null ? String(profile.phone) : "",
          gender: profile.gender === "Male" || profile.gender === "Female" ? profile.gender : null,
          nickname: profile.nickname || "",
          address: profile.address || "",
        });
      })
      .catch((err) => {
        console.error("Error loading profile:", err);
        showAlert("Greška prilikom učitavanja profila", "error");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const handleLogout = async () => {
    await unregisterPushToken();
    localStorage.clear();
    window.location.replace("/login");
  };

  const handleGenderChange = (which) => {
    setGender({ male: which === "male", female: which === "female" });
  };

  const handleSave = async () => {
    if (!firstName.trim()) {
      showAlert("Ime je obavezno", "error");
      return;
    }
    if (!lastName.trim()) {
      showAlert("Prezime je obavezno", "error");
      return;
    }

    setSaving(true);
    try {
      const data = {
        first_name: firstName,
        last_name: lastName,
        phone,
        gender: genderValue,
        nickname,
        address,
      };
      await ApiMethods.post(`employee/me/update`, data, token);

      // Matches what was just sent — re-disables the save button until the next real edit,
      // same idea as re-fetching, minus the round-trip.
      setSavedProfile({ firstName, lastName, phone, gender: genderValue, nickname, address });

      // Reflect immediately everywhere the logged-in trainer's identity is shown — no manual
      // page refresh. Photo already updates this way via ProfilePhotoPicker's own event; name
      // needs the same treatment since AppShell reads it from localStorage too.
      localStorage.setItem("firstName", firstName);
      localStorage.setItem("lastName", lastName);
      window.dispatchEvent(
        new CustomEvent(PROFILE_UPDATED_EVENT, { detail: { firstName, lastName } })
      );

      showAlert("Profil je uspešno sačuvan", "success");
    } catch (err) {
      console.error("Error saving profile:", err);
      showAlert("Greška prilikom čuvanja izmena", "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <AppShell active="profile">
      <MobileHeader title="Profil" />

      <div className="hf-set-page">
        <div className="hf-set-header">
          <h1 className="hf-set-title">Profil</h1>
        </div>

        {loading ? (
          <div className="hf-set-loading">Učitavanje...</div>
        ) : (
          <div className="hf-set-layout">
            <div className="hf-set-summary-card">
              <button
                type="button"
                className="hf-set-avatar-wrap"
                aria-label="Promeni profilnu fotografiju"
                onClick={() => setPickerOpen(true)}
              >
                {photoUrl ? (
                  <img
                    className="hf-set-avatar hf-set-avatar-img"
                    src={photoUrl}
                    alt=""
                  />
                ) : (
                  <div className="hf-set-avatar">{initials || "?"}</div>
                )}
                <span className="hf-set-avatar-camera">
                  <PhotoCameraIcon sx={{ fontSize: 13 }} />
                </span>
              </button>
              <div className="hf-set-summary-info">
                <div className="hf-set-profile-name">
                  {firstName || lastName
                    ? `${firstName} ${lastName}`.trim()
                    : "Radnik"}
                </div>
                <div className="hf-set-profile-role">Trener</div>
              </div>

              <div className="hf-set-summary-actions">
                <button
                  type="button"
                  className="hf-set-photo-btn"
                  onClick={() => setPickerOpen(true)}
                >
                  <PhotoCameraIcon fontSize="small" />
                  Promeni sliku
                </button>
                {/* Tablet/Desktop only (768px+) — Mobile shows its own full-width copy of this
                    button at the very bottom of the page instead (see below the form card),
                    since there's no room for it alongside the avatar/name here on a narrow
                    screen. */}
                <button
                  type="button"
                  className="hf-set-logout-btn hf-set-logout-btn-top"
                  onClick={handleLogout}
                >
                  <LogoutIcon fontSize="small" />
                  Odjavi se
                </button>
              </div>
            </div>

            <div className="hf-set-form-card">
              <div className="hf-set-section-head">
                <span className="hf-set-section-icon">
                  <PersonOutlineIcon sx={{ fontSize: 18 }} />
                </span>
                <div className="hf-set-section-text">
                  <div className="hf-set-section-title">Lični podaci</div>
                  <div className="hf-set-section-desc">
                    Ovi podaci se koriste u aplikaciji i nisu javno vidljivi.
                  </div>
                </div>
              </div>

              <div className="hf-set-row">
                <div className="hf-set-field">
                  <label className="hf-set-label">Ime *</label>
                  <input
                    className="hf-set-input"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                  />
                </div>
                <div className="hf-set-field">
                  <label className="hf-set-label">Prezime *</label>
                  <input
                    className="hf-set-input"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                  />
                </div>
              </div>

              <div className="hf-set-row">
                <div className="hf-set-field">
                  <label className="hf-set-label">Nadimak</label>
                  <input
                    className="hf-set-input"
                    placeholder="npr. Jordi"
                    value={nickname}
                    onChange={(e) => setNickname(e.target.value)}
                  />
                </div>
                <div className="hf-set-field">
                  <label className="hf-set-label">Broj telefona</label>
                  <div className="hf-set-input-icon-wrap">
                    <LocalPhoneIcon
                      className="hf-set-input-icon"
                      sx={{ fontSize: 16 }}
                    />
                    <input
                      className="hf-set-input hf-set-input-with-icon"
                      type="tel"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                    />
                  </div>
                </div>
              </div>

              <div className="hf-set-row">
                <div className="hf-set-field">
                  <label className="hf-set-label">Email adresa</label>
                  <div className="hf-set-input-icon-wrap">
                    <EmailIcon className="hf-set-input-icon" sx={{ fontSize: 16 }} />
                    <input
                      className="hf-set-input hf-set-input-with-icon"
                      type="email"
                      value={email}
                      readOnly
                    />
                  </div>
                  <p className="hf-set-helper-text">
                    Email adresa se ne može menjati nakon kreiranja naloga.
                  </p>
                </div>
                <div className="hf-set-field">
                  <label className="hf-set-label">Pol</label>
                  <div className="hf-set-gender-row">
                    <button
                      type="button"
                      className={`hf-set-gender-chip${gender.male ? " active" : ""}`}
                      onClick={() => handleGenderChange("male")}
                    >
                      Muško
                    </button>
                    <button
                      type="button"
                      className={`hf-set-gender-chip${gender.female ? " active" : ""}`}
                      onClick={() => handleGenderChange("female")}
                    >
                      Žensko
                    </button>
                  </div>
                </div>
              </div>

              {/* Tablet/Desktop (768px+, see profileShell.css): same row as the save button,
                  since it's the last field and there's otherwise a lot of empty space beside it
                  — Mobile keeps them stacked (the button's own existing full-width look). */}
              <div className="hf-set-address-row">
                <div className="hf-set-field">
                  <label className="hf-set-label">Adresa</label>
                  <div className="hf-set-input-icon-wrap">
                    <AddLocationAltIcon
                      className="hf-set-input-icon"
                      sx={{ fontSize: 16 }}
                    />
                    <input
                      className="hf-set-input hf-set-input-with-icon"
                      value={address}
                      onChange={(e) => setAddress(e.target.value)}
                    />
                  </div>
                </div>

                <button
                  type="button"
                  className="hf-set-save-btn"
                  disabled={saving || !isDirty}
                  onClick={handleSave}
                >
                  {saving ? "Čuvanje..." : "Sačuvaj izmene"}
                </button>
              </div>
            </div>

            <div className="hf-set-form-card">
              <div className="hf-set-section-head">
                <span className="hf-set-section-icon">
                  <NotificationsNoneIcon sx={{ fontSize: 18 }} />
                </span>
                <div className="hf-set-section-text">
                  <div className="hf-set-section-title">Push obaveštenja</div>
                  <div className="hf-set-section-desc">
                    Otkazani termini i zahtevi za pakete stižu kao obaveštenja i kada
                    aplikacija nije otvorena.
                  </div>
                </div>
              </div>

              {pushStatus === "subscribed" && (
                <p className="hf-set-helper-text">
                  Uključeno na ovom uređaju.
                </p>
              )}
              {pushStatus === "denied" && (
                <p className="hf-set-helper-text">
                  Obaveštenja su blokirana za ovaj sajt. Dozvolite ih u podešavanjima
                  pregledača, pa osvežite stranicu.
                </p>
              )}
              {pushStatus === "unsupported" && (
                <p className="hf-set-helper-text">
                  Ovaj pregledač ili uređaj ne podržava push obaveštenja.
                </p>
              )}
              {(pushStatus === "default" || pushStatus === "unregistered") && (
                <button
                  type="button"
                  className="hf-set-save-btn"
                  disabled={pushBusy}
                  onClick={handleSubscribePush}
                >
                  {pushBusy ? "Uključivanje..." : "Uključi push obaveštenja"}
                </button>
              )}
            </div>

          </div>
        )}
      </div>

      <ProfilePhotoPicker
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        currentPhotoUrl={photoUrl}
        onUpdated={(url) => setPhotoUrl(url)}
      />
    </AppShell>
  );
};

export default Profile;
